import {
  Component,
  input,
  output,
  signal,
  inject,
  computed,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { AseguradoraRepository } from 'src/app/domain/service/aseguradora/aseguradora-repository';
import {
  MAX_IDENTIFICACION_BUSQUEDA,
  identificacionBusquedaKeypress,
  sanitizeIdentificacionBusqueda,
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import {
  Aseguradora,
  AseguradoraFiltro,
  getAseguradoraNombreDisplay
} from '@model/aseguradora/aseguradora';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS,
  TIPO_IDENTIFICACION
} from 'src/app/common/constants-ejb';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { sanitizeIdentificacion } from 'src/app/common/input-utils';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { buscadorAseguradoraPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { resolveSearchErrorMessage } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';

/**
 * Resultado de aseguradora seleccionada
 */
export interface AseguradoraSeleccionada {
  nmconsecutivo: number;
  cdpersona: number;
  tipoIdentificacion: string;
  numeroIdentificacion: string;
  nombreComercial: string;
}

/** Campos de nombre de persona natural (tipo de identificación distinto de NIT). */
type CampoNombrePersona = 'nombre1' | 'nombre2' | 'apellido1' | 'apellido2';

/** Campos de texto libre del filtro (seguridad-girop §3). */
type CampoTextoAseguradora = 'nombreComercial' | CampoNombrePersona;

/** Longitud máxima del nombre comercial (backend: 150). */
const MAX_NOMBRE_COMERCIAL = 150;

/** Longitud máxima de nombres y apellidos (backend: 50). */
const MAX_NOMBRE_PERSONA = 50;

const CAMPOS_NOMBRE_PERSONA: ReadonlyArray<{ key: CampoNombrePersona; label: string }> = [
  { key: 'nombre1', label: 'Primer Nombre' },
  { key: 'nombre2', label: 'Segundo Nombre' },
  { key: 'apellido1', label: 'Primer Apellido' },
  { key: 'apellido2', label: 'Segundo Apellido' }
];

/**
 * Diálogo de búsqueda de aseguradoras.
 *
 * Legacy: ClientesSearchViewImpl -> PersonSearcherInterfaceImpl -> IdentificationViewImpl.
 *
 * Filtros:
 * - Número de identificación (coincidencia exacta con P.NMIDENTIFICACION; alfanumérico en mayúsculas)
 * - Con tipo NIT: Nombre Comercial (búsqueda parcial sobre P.DSNOMBRE_COMERCIAL)
 * - Con otro tipo: primer/segundo nombre y primer/segundo apellido (búsqueda parcial)
 * - Activo
 *
 * @author Sura - Migración GWT a Spring Boot
 */
@Component({
  selector: 'app-busqueda-aseguradora-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Filtros Consulta de Aseguradoras"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '48rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <div
        class="border dark:border-2 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-600"
      >
        <div class="font-medium text-gray-700 dark:text-gray-200 mb-3 text-sm">
          Filtros de Búsqueda
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Identificación</label
            >
            <p-dropdown
              [ngModel]="filtro.tipoIdentificacion"
              (ngModelChange)="onTipoIdentificacionCambio($event)"
              [options]="tiposIdentificacion()"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Tipo"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%' }"
              [showClear]="true"
              appendTo="body"
            >
            </p-dropdown>
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Número</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.numeroIdentificacion"
              (keydown)="soloIdentificacion($event)"
              (input)="sanitizarIdentificacion()"
              class="w-full"
              [maxlength]="maxIdentificacion"
            />
          </div>
        </div>

        <!-- Legacy IdentificationViewImpl.changeVisibleFields(): sin tipo no se muestran
             nombres; con NIT, nombre comercial; con otro tipo, nombres y apellidos. -->
        @if (filtro.tipoIdentificacion) {
          @if (esNit()) {
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label
                  for="aseguradoraNombreComercial"
                  class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                  >Nombre Comercial</label
                >
                <input
                  pInputText
                  id="aseguradoraNombreComercial"
                  [(ngModel)]="filtro.nombreComercial"
                  (input)="sanitizarCampo('nombreComercial')"
                  class="w-full"
                  [maxlength]="maxNombreComercial"
                />
              </div>
              <div></div>
            </div>
          } @else {
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              @for (campo of camposNombrePersona; track campo.key) {
                <div>
                  <label
                    [for]="'aseguradora-' + campo.key"
                    class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                    >{{ campo.label }}</label
                  >
                  <input
                    pInputText
                    [id]="'aseguradora-' + campo.key"
                    [(ngModel)]="filtro[campo.key]"
                    (input)="sanitizarCampo(campo.key)"
                    class="w-full"
                    [maxlength]="maxNombrePersona"
                  />
                </div>
              }
            </div>
          }
        }

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Activo</label
            >
            <div class="flex flex-wrap gap-4 mt-2">
              <div class="flex items-center">
                <p-radioButton
                  name="activo"
                  value="S"
                  [(ngModel)]="filtro.activo"
                  inputId="activoSi"
                ></p-radioButton>
                <label for="activoSi" class="ml-1 text-sm dark:text-gray-300"
                  >Sí</label
                >
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="activo"
                  value="N"
                  [(ngModel)]="filtro.activo"
                  inputId="activoNo"
                ></p-radioButton>
                <label for="activoNo" class="ml-1 text-sm dark:text-gray-300"
                  >No</label
                >
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="activo"
                  value=""
                  [(ngModel)]="filtro.activo"
                  inputId="activoTodos"
                ></p-radioButton>
                <label for="activoTodos" class="ml-1 text-sm dark:text-gray-300"
                  >Todos</label
                >
              </div>
            </div>
          </div>
          <div></div>
        </div>

        <div
          class="flex flex-wrap gap-2 sm:gap-3 mt-4 justify-end sm:justify-start w-full"
        >
          <button
            pButton
            icon="pi pi-eraser"
            label="Limpiar"
            class="p-button-sm p-button-secondary p-button-outlined min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-sm min-h-[44px]"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
        </div>
      </div>

      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300':
              mensajeTipo() === 'warning',
            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300':
              mensajeTipo() === 'info',
            'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300':
              mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <div class="border dark:border-2 rounded dark:border-gray-600">
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b bg-gray-50 dark:bg-gray-800 dark:border-gray-700 text-sm flex justify-between items-center"
        >
          <span>Listado de Aseguradoras</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            </span>
          }
        </div>

        @if (!loading() && items().length > 0) {
          <div class="md:hidden space-y-3 p-3">
            @for (item of items(); track item.nmconsecutivo) {
              <div
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
              >
                <div class="mb-3 min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Nombre
                  </div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                    [attr.title]="tituloTabla(getNombreDisplay(item))"
                  >
                    {{ textoTabla(getNombreDisplay(item)) }}
                  </div>
                </div>
                <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Tipo ID:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(getTipoIdentificacion(item))
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Número ID:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(getNumeroIdentificacion(item))
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Activo:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      item.snactivo === 'S' ? 'Sí' : 'No'
                    }}</span>
                  </div>
                </div>
                <p-button
                  label="Seleccionar"
                  icon="pi pi-check"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="seleccionarItem(item)"
                ></p-button>
              </div>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [value]="items()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [scrollHeight]="tableConfig().responsive.scrollHeight || '250px'"
              [selectionMode]="tableSelectionMode()"
              [(selection)]="seleccionado"
              (onRowSelect)="onRowSelect($event)"
              [tableStyle]="{
                'min-width': tableConfig().responsive.minWidth || 'max-content'
              }"
              [dataKey]="
                (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
              "
              [styleClass]="tableStyleClass()"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Tipo ID"
                      >Tipo ID</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Número ID"
                      >Número ID</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Nombre">Nombre</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Activo">Activo</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Seleccionar"
                      >Seleccionar</span
                    >
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr [pSelectableRow]="item">
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(getTipoIdentificacion(item))"
                      >{{ textoTabla(getTipoIdentificacion(item)) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(getNumeroIdentificacion(item))"
                      >{{ textoTabla(getNumeroIdentificacion(item)) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(getNombreDisplay(item))"
                      >{{ textoTabla(getNombreDisplay(item)) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="item.snactivo === 'S' ? 'Sí' : 'No'"
                      >{{ item.snactivo === 'S' ? 'Sí' : 'No' }}</span
                    >
                  </td>
                  <td>
                    <button
                      pButton
                      label="Seleccionar"
                      class="p-button-sm p-button-text"
                      (click)="seleccionarItem(item)"
                    ></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <!-- Paginación Keyset/Cursor (skill acceso-datos-oracle §7) -->
          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPaginas()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="loadPreviousPage()"
            (next)="loadNextPage()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }

        @if (!loading() && items().length === 0 && hasBuscado()) {
          <div class="text-center text-gray-500 dark:text-gray-400 p-4">
            No se encontraron aseguradoras con los criterios especificados
          </div>
        }

        @if (!loading() && items().length === 0 && !hasBuscado()) {
          <div class="text-center text-gray-500 dark:text-gray-400 p-4">
            No hay datos
          </div>
        }
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaAseguradoraDialogComponent implements OnInit {
  protected readonly sanitizeIdentificacion = sanitizeIdentificacion;
  protected readonly sanitizeText = sanitizeText;

  tipoIdentificacionDefault = input<string>('');
  activoDefault = input<string>('S');
  legacyFacturacionMode = input(false);

  // Inputs/Outputs
  visible = true; // Inicia visible cuando se renderiza
  seleccionado: Aseguradora | null = null;

  /** Output: aseguradora seleccionada */
  aseguradoraSelected = output<AseguradoraSeleccionada>();

  /** Output: diálogo cerrado sin selección */
  dialogClosed = output<void>();

  // Servicios
  private readonly repository = inject(AseguradoraRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);

  // Estado
  loading = signal(false);
  items = signal<Aseguradora[]>([]);
  tiposIdentificacion = signal<CatalogoOption[]>([]);
  mensaje = signal('');
  mensajeTipo = signal<'info' | 'warning' | 'error'>('info');
  hasBuscado = signal(false);

  /** Longitud máxima del número de identificación (backend: 20). */
  readonly maxIdentificacion = MAX_IDENTIFICACION_BUSQUEDA;
  readonly maxNombreComercial = MAX_NOMBRE_COMERCIAL;
  readonly maxNombrePersona = MAX_NOMBRE_PERSONA;
  readonly camposNombrePersona = CAMPOS_NOMBRE_PERSONA;

  // Filtros - Por defecto activo = 'S' (Sí)
  filtro: AseguradoraFiltro = {
    tipoIdentificacion: undefined,
    numeroIdentificacion: undefined,
    nombreComercial: undefined,
    nombre1: undefined,
    nombre2: undefined,
    apellido1: undefined,
    apellido2: undefined,
    activo: 'S'
  };

  /**
   * Persona jurídica = tipo NIT. El legado alterna el bloque de nombres con
   * este mismo criterio ({@code ConsultarClientesViewImpl.mostrarOcultarCampos}).
   */
  protected mostrarCamposPersonaJuridica(): boolean {
    return this.filtro.tipoIdentificacion === TIPO_IDENTIFICACION.NIT;
  }

  /**
   * Al cambiar el tipo se limpia el bloque de nombres que deja de aplicar —
   * el legado tampoco envía esos campos ({@code getDTO()} escoge uno u otro
   * grupo según el tipo).
   */
  protected onTipoIdentificacionCambio(tipo: string | undefined): void {
    this.filtro.tipoIdentificacion = tipo ?? undefined;
    if (this.mostrarCamposPersonaJuridica()) {
      this.filtro.nombre1 = undefined;
      this.filtro.nombre2 = undefined;
      this.filtro.apellido1 = undefined;
      this.filtro.apellido2 = undefined;
    } else {
      this.filtro.nombreComercial = undefined;
    }
  }

  // Paginación por cursor (igual que busqueda-cliente-dialog)
  totalRegistros = signal(0);
  paginaActual = signal(1);
  rowsPerPage = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  private nextCursorActual = signal<string | null>(null);
  private previousCursorActual = signal<string | null>(null);

  // Computed para paginación
  totalPaginas = computed(() => {
    const total = this.totalRegistros();
    const size = this.rowsPerPage();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.paginaActual() - 1) * this.rowsPerPage() + 1;
  });

  registroFin = computed(() =>
    Math.min(this.paginaActual() * this.rowsPerPage(), this.totalRegistros())
  );

  // paginaActual es 1-based y solo alimenta las etiquetas. Los botones
  // Anterior/Siguiente dependen de la respuesta del servidor (skill
  // frontend-girop §17.7).
  hasPrevious = signal(false);
  hasNext = signal(false);

  readonly tableConfig = computed(() =>
    buscadorAseguradoraPreset.createConfig({
      data: this.items() as unknown as Record<string, unknown>[],
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-buscador-aseguradora',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage:
          'No se encontraron aseguradoras con los criterios especificados'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de aseguradoras',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly tableSelectionMode = computed<'single' | 'multiple' | undefined>(
    () => {
      const mode = this.tableConfig().selection.mode;
      if (mode === 'single' || mode === 'multiple') {
        return mode;
      }
      return undefined;
    }
  );

  async ngOnInit() {
    this.aplicarValoresDefault();
    await this.cargarTiposIdentificacion();
  }

  private aplicarValoresDefault(): void {
    const tipoDefault = (this.tipoIdentificacionDefault() || '').trim();
    const activoDefault = this.activoDefault();

    if (tipoDefault) {
      this.filtro.tipoIdentificacion = tipoDefault;
    }
    if (activoDefault !== undefined && activoDefault !== null) {
      this.filtro.activo = activoDefault;
    }
  }

  private async cargarTiposIdentificacion() {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
    } catch (error) {
      console.error('Error cargando tipos de identificación:', error);
    }
  }

  /**
   * Abre el diálogo de búsqueda
   */
  open(): void {
    this.visible = true;
    this.limpiarFiltros();
  }

  /**
   * Cierra el diálogo
   */
  close(): void {
    this.visible = false;
  }

  /**
   * Ejecuta la búsqueda de aseguradoras (primera página)
   */
  async buscar(): Promise<void> {
    this.sanitizarIdentificacion();
    for (const campo of ['nombreComercial', ...CAMPOS_NOMBRE_PERSONA.map((c) => c.key)] as const) {
      this.sanitizarCampo(campo, true);
    }
    const errorLongitud = this.validarLongitudes();
    if (errorLongitud) {
      this.mensaje.set(errorLongitud);
      this.mensajeTipo.set('error');
      return;
    }
    this.hasBuscado.set(true);
    // Búsqueda nueva: siempre página 1
    await this.buscarPagina(null, 'NEXT', 1);
  }

  /** Legacy: Constants.LIST_VALUE_TYPE_ID_NIT = "N". */
  esNit(): boolean {
    return this.filtro.tipoIdentificacion === TIPO_IDENTIFICACION.NIT;
  }

  /**
   * Filtro que se envía al backend. Replica IdentificationViewImpl: los campos
   * visibles dependen del tipo (NIT -> nombre comercial; otro -> nombres y
   * apellidos; sin tipo -> ninguno) y los textos van en mayúsculas
   * (getDataFromFields). Los campos ocultos no se envían.
   */
  private construirFiltro(): AseguradoraFiltro {
    const conTipo = !!this.filtro.tipoIdentificacion;
    const esNit = this.esNit();
    const texto = (valor: string | undefined) =>
      valor?.trim() ? valor.trim().toUpperCase() : undefined;
    return {
      tipoIdentificacion: this.filtro.tipoIdentificacion,
      numeroIdentificacion: this.filtro.numeroIdentificacion,
      activo: this.filtro.activo,
      nombreComercial: esNit ? texto(this.filtro.nombreComercial) : undefined,
      nombre1: conTipo && !esNit ? texto(this.filtro.nombre1) : undefined,
      nombre2: conTipo && !esNit ? texto(this.filtro.nombre2) : undefined,
      apellido1: conTipo && !esNit ? texto(this.filtro.apellido1) : undefined,
      apellido2: conTipo && !esNit ? texto(this.filtro.apellido2) : undefined
    };
  }

  /** Validación de longitud máxima (seguridad-girop §2.1) antes de consultar. */
  private validarLongitudes(): string | null {
    if ((this.filtro.nombreComercial?.length ?? 0) > MAX_NOMBRE_COMERCIAL) {
      return `Nombre Comercial: longitud permitida entre 1 y ${MAX_NOMBRE_COMERCIAL} caracteres.`;
    }
    for (const campo of CAMPOS_NOMBRE_PERSONA) {
      if ((this.filtro[campo.key]?.length ?? 0) > MAX_NOMBRE_PERSONA) {
        return `${campo.label}: longitud permitida entre 1 y ${MAX_NOMBRE_PERSONA} caracteres.`;
      }
    }
    return null;
  }

  /**
   * Número de identificación: letras y dígitos, sin espacios ni separadores.
   * Legacy: IdentificationViewImpl.setRegex() (Constants.REGEX_ALPHANUMERIC).
   */
  soloIdentificacion(event: KeyboardEvent): void {
    if (!identificacionBusquedaKeypress(event)) {
      event.preventDefault();
    }
  }

  /**
   * Limpia el número de identificación (también lo pegado) y lo pasa a mayúsculas.
   * Legacy: IdentificationViewImpl.getDataFromFields() — toUpperCase().
   */
  sanitizarIdentificacion(): void {
    if (this.filtro.numeroIdentificacion === undefined) return;
    this.filtro.numeroIdentificacion = sanitizeIdentificacionBusqueda(
      this.filtro.numeroIdentificacion
    );
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(
    campo: CampoTextoAseguradora,
    recortar = false
  ): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  private async buscarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const response = await this.repository.buscarAseguradoras(
        this.construirFiltro(),
        cursor,
        this.rowsPerPage(),
        direction
      );

      this.items.set(response.content);
      this.totalRegistros.set(response.totalElements ?? 0);
      this.nextCursorActual.set(response.nextCursor ?? null);
      this.previousCursorActual.set(response.previousCursor ?? null);
      this.hasNext.set(response.hasNext);
      this.hasPrevious.set(response.hasPrevious);

      // La página solo cambia tras una respuesta exitosa. Con LAST se calcula
      // a partir del total que devuelve el servidor.
      if (direction === 'LAST') {
        const size = this.rowsPerPage();
        const total = response.totalElements ?? 0;
        this.paginaActual.set(
          size > 0 && total > 0 ? Math.max(1, Math.ceil(total / size)) : 1
        );
      } else {
        this.paginaActual.set(pagina);
      }

      if (response.content.length === 0) {
        this.mensaje.set(
          'No se encontraron aseguradoras con los criterios especificados'
        );
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando aseguradoras:', error);
      this.mensaje.set(
        resolveSearchErrorMessage(error, 'Error al buscar aseguradoras')
      );
      this.mensajeTipo.set('error');
      this.hasNext.set(false);
      this.hasPrevious.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Limpia los filtros y resultados
   */
  limpiarFiltros(): void {
    this.filtro = {
      tipoIdentificacion: undefined,
      numeroIdentificacion: undefined,
      nombreComercial: undefined,
      nombre1: undefined,
      nombre2: undefined,
      apellido1: undefined,
      apellido2: undefined,
      activo: 'S'
    };
    // "Limpiar" deja los filtros como al abrir, no como si no hubiera
    // configuración: el legado vuelve al tipo por defecto (CC) y a "Todos".
    this.aplicarValoresDefault();
    this.items.set([]);
    this.totalRegistros.set(0);
    this.paginaActual.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.rowsPerPage.set(DEFAULT_ROWS_PER_PAGE);
    this.selectedPageSize = DEFAULT_ROWS_PER_PAGE;
    this.nextCursorActual.set(null);
    this.previousCursorActual.set(null);
    this.mensaje.set('');
    this.hasBuscado.set(false);
    this.seleccionado = null;
    this.hasNext.set(false);
    this.hasPrevious.set(false);
  }

  // === Métodos de paginación (Keyset/Cursor) ===

  onPageSizeChange(): void {
    this.rowsPerPage.set(this.selectedPageSize);
    this.paginaActual.set(1);
    if (this.hasBuscado()) {
      void this.buscarPagina(null, 'NEXT', 1);
    }
  }

  irPrimeraPagina(): void {
    if (this.loading()) {
      return;
    }
    void this.buscarPagina(null, 'NEXT', 1);
  }

  loadPreviousPage(): void {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    void this.buscarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  loadNextPage(): void {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    void this.buscarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.paginaActual() + 1
    );
  }

  irUltimaPagina(): void {
    if (this.loading() || this.totalRegistros() === 0) {
      return;
    }
    void this.buscarPagina(null, 'LAST');
  }

  /**
   * Maneja la selección de una fila
   */
  onRowSelect(event: TableRowSelectEvent<Aseguradora>): void {
    if (event.data && !Array.isArray(event.data)) {
      this.seleccionado = event.data;
    }
  }

  /**
   * Obtiene el nombre para mostrar de una aseguradora
   */
  getNombreDisplay(aseguradora: Aseguradora): string {
    return getAseguradoraNombreDisplay(aseguradora);
  }

  /**
   * Obtiene la descripción del tipo de identificación para mostrar en el grid.
   * Usa el campo tipoIdentificacion que contiene la descripción (ej: "NIT").
   */
  getTipoIdentificacion(aseguradora: Aseguradora): string {
    return aseguradora.tipoIdentificacion || '';
  }

  /**
   * Obtiene el número de identificación para mostrar en el grid.
   */
  getNumeroIdentificacion(aseguradora: Aseguradora): string {
    return aseguradora.numeroIdentificacion || '';
  }

  /**
   * Selecciona una aseguradora directamente desde el link de la tabla
   */
  seleccionarItem(item: Aseguradora): void {
    const resultado: AseguradoraSeleccionada = {
      nmconsecutivo: item.nmconsecutivo,
      cdpersona: item.cdpersona,
      tipoIdentificacion: item.cdtipoIdentificacion || '',
      numeroIdentificacion: item.numeroIdentificacion,
      nombreComercial: getAseguradoraNombreDisplay(item)
    };
    this.aseguradoraSelected.emit(resultado);
    this.close();
  }

  /**
   * Extrae el tipo de identificación del campo DNI (ej: "NIT - 800256161" -> "NIT")
   * @deprecated Ya no se usa, el backend devuelve los campos separados
   */
  private extraerTipoIdentificacion(dni: string): string {
    if (!dni) return '';
    const partes = dni.split(' - ');
    return partes.length > 1 ? partes[0].trim() : '';
  }

  /**
   * Extrae el número de identificación del campo DNI (ej: "NIT - 800256161" -> "800256161")
   * @deprecated Ya no se usa, el backend devuelve los campos separados
   */
  private extraerNumeroIdentificacion(dni: string): string {
    if (!dni) return dni;
    const partes = dni.split(' - ');
    return partes.length > 1 ? partes[1].trim() : dni;
  }

  /**
   * Emite la aseguradora seleccionada y cierra el diálogo
   */
  onSeleccionar(): void {
    if (this.seleccionado) {
      this.seleccionarItem(this.seleccionado);
    }
  }

  /**
   * Cierra el diálogo sin selección
   */
  onCancelar(): void {
    this.dialogClosed.emit();
    this.close();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
